import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Receipt, Trash2, Sparkles, Paperclip, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const CATEGORIES = ["maintenance", "taxes", "insurance", "utilities", "management", "mortgage", "other"];

export default function Expenses() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ recurring: false });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const invoiceRef = useRef(null);

  const { data: items = [] } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });
  const { data: properties = [] } = useQuery({ queryKey: ["properties"], queryFn: async () => (await api.get("/properties")).data });
  const propName = (id) => properties.find((p) => p.id === id)?.name || "—";

  const create = useMutation({
    mutationFn: (payload) => api.post("/expenses", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setForm({ recurring: false }); setInvoiceFile(null); toast.success("OK");
    },
    onError: () => toast.error("Error"),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/expenses/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });

  const runOcr = async () => {
    if (!invoiceFile) return toast.error(t("attach_invoice"));
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", invoiceFile);
      const res = await api.post("/ai/ocr-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        category: res.data.category || f.category,
        amount: res.data.amount || f.amount,
        date: res.data.date || f.date,
        description: res.data.description || f.description,
      }));
      toast.success("IA ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR error");
    } finally {
      setOcrBusy(false);
    }
  };

  const submit = async () => {
    if (!form.amount) return toast.error(t("amount"));
    setSaving(true);
    try {
      let documentId = null;
      if (invoiceFile) {
        const fd = new FormData();
        fd.append("file", invoiceFile);
        fd.append("category", "invoice");
        const up = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        documentId = up.data.id;
      }
      await create.mutateAsync({
        property_id: form.property_id || null,
        category: form.category || "maintenance",
        description: form.description || "",
        amount: parseFloat(form.amount) || 0,
        date: form.date || null,
        recurring: !!form.recurring,
        document_id: documentId,
      });
    } catch (e) {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">{t("nav_expenses")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-btn" className="rounded-sm gap-2">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">{t("add_expense")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display text-2xl">{t("add_expense")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="space-y-2"><Label>{t("category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category" className="rounded-sm"><SelectValue placeholder="maintenance" /></SelectTrigger>
                  <SelectContent className="bg-popover">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>{t("amount")}</Label>
                <Input type="number" data-testid="expense-amount" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm" /></div>
              <div className="col-span-2 space-y-2"><Label>{t("property")}</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="expense-property" className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-popover">{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-2 space-y-2"><Label>{t("description")}</Label>
                <Input data-testid="expense-desc" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("date")}</Label>
                <Input type="date" data-testid="expense-date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" /></div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={!!form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} data-testid="expense-recurring" />
                <Label>{t("recurring")}</Label>
              </div>
            </div>

            <div className="rounded-sm border border-dashed border-border p-4 space-y-3">
              <p className="text-xs text-muted-foreground">{t("ai_ocr_hint")}</p>
              <input ref={invoiceRef} type="file" accept="image/*" hidden data-testid="expense-invoice-input"
                onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => invoiceRef.current?.click()}
                  data-testid="expense-attach-btn" className="rounded-sm gap-2">
                  <Paperclip className="h-4 w-4" /> {t("attach_invoice")}
                </Button>
                {invoiceFile && <span className="text-xs text-muted-foreground truncate max-w-[140px]">{invoiceFile.name}</span>}
                <Button type="button" variant="secondary" size="sm" onClick={runOcr} disabled={!invoiceFile || ocrBusy}
                  data-testid="expense-ocr-btn" className="rounded-sm gap-2">
                  <Sparkles className="h-4 w-4" /> {ocrBusy ? t("analyzing") : t("ai_autofill")}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("cancel")}</Button>
              <Button onClick={submit} data-testid="expense-save" disabled={saving || create.isPending} className="rounded-sm">{saving ? "..." : t("save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Receipt className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_generic")}</p>
        </Card>
      ) : (
        <>
          <Card className="hidden sm:block rounded-sm border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("category")}</TableHead>
                  <TableHead>{t("description")}</TableHead>
                  <TableHead>{t("property")}</TableHead>
                  <TableHead>{t("date")}</TableHead>
                  <TableHead className="text-right">{t("amount")}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell>
                      <Badge variant="secondary" className="rounded-sm">{e.category}</Badge>
                      {e.recurring && <Badge variant="outline" className="rounded-sm ml-1">↻</Badge>}
                    </TableCell>
                    <TableCell>{e.description || "—"}</TableCell>
                    <TableCell>{propName(e.property_id)}</TableCell>
                    <TableCell>{fmtDate(e.date)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[hsl(var(--destructive))]">-{fmtCurrency(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      {e.document_id && (
                        <Button variant="ghost" size="icon" data-testid={`invoice-${e.id}`}
                          onClick={() => window.open(`${API}/documents/${e.document_id}/download`, "_blank")}
                          title={t("invoice")}><Download className="h-4 w-4" /></Button>
                      )}
                      <Button variant="ghost" size="icon" data-testid={`del-expense-${e.id}`} onClick={() => remove.mutate(e.id)}
                        className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="sm:hidden grid grid-cols-1 gap-4">
            {items.map((e) => (
              <Card key={e.id} data-testid={`expense-card-${e.id}`} className="p-4 rounded-sm border-border">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Badge variant="secondary" className="rounded-sm">{e.category}</Badge>
                    {e.recurring && <Badge variant="outline" className="rounded-sm ml-1">↻</Badge>}
                    <p className="text-sm text-muted-foreground truncate mt-1">{e.description || "—"}</p>
                  </div>
                  <span className="tabular-nums text-[hsl(var(--destructive))] shrink-0">-{fmtCurrency(e.amount)}</span>
                </div>
                <div className="flex items-center justify-between mt-3 text-sm">
                  <span className="text-muted-foreground">{t("property")}</span>
                  <span>{propName(e.property_id)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 text-sm">
                  <span className="text-muted-foreground">{t("date")}</span>
                  <span>{fmtDate(e.date)}</span>
                </div>
                <div className="flex items-center justify-end gap-1 mt-4 pt-3 border-t border-border">
                  {e.document_id && (
                    <Button variant="ghost" size="icon" data-testid={`invoice-mobile-${e.id}`}
                      onClick={() => window.open(`${API}/documents/${e.document_id}/download`, "_blank")}
                      title={t("invoice")}><Download className="h-4 w-4" /></Button>
                  )}
                  <Button variant="ghost" size="icon" data-testid={`del-expense-mobile-${e.id}`} onClick={() => remove.mutate(e.id)}
                    className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
